import React, { useEffect, useState } from "react";
import { Outlet, Link, useNavigate } from "react-router-dom";
import {
  Menu,
  MenuItem,
  MenuMenu,
  GridRow,
  Container,
  Grid,
  Icon,
  Sidebar,
  Segment,
  Button,
} from "semantic-ui-react";
import deltaLogo from "../pictures/logo.png";
import "./Layout.css";
import { Helmet } from "react-helmet-async";
import "semantic-ui-css/semantic.min.css";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";

const Layout = ({ signOut }) => {
  const [visible, setVisible] = useState(false);
  const [fullName, setFullName] = useState("");
  const [access, setAccess] = useState("");
  const [currentView, setCurrentView] = useState("agent");
  const navigate = useNavigate();

  const handleSidebarToggle = () => setVisible(!visible);

  useEffect(() => {
    async function loadUser() {
      try {
        const user = await getCurrentUser();
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;

        const name = idToken?.payload?.name;
        const userAccess = idToken?.payload?.["custom:access"] || "agent";

        setFullName(name || user.username || "User");
        setAccess(userAccess);

        // Set default screen based on access
        if (userAccess === "employee") {
          setCurrentView("employee");
        } else {
          // "agent" and "both" default to agent view
          setCurrentView("agent");
        }
      } catch (err) {
        console.error("Error fetching user info:", err);
      }
    }

    loadUser();
  }, []);

  const isAgentView = currentView === "agent";
  const homePath = isAgentView ? "/" : "/ehome";

  return (
    <div fluid className="content-container">
      <Helmet>
        <title>Delta Group Portal</title>
        <meta name="description" content="" />
        <link rel="canonical" href="/" />
      </Helmet>

      <Container fluid className="menu-container">
        <Grid verticalAlign="middle">
          <GridRow fluid className="top-row">
            <Grid.Column width={2} only="mobile tablet" textAlign="left">
              <Button className="button-hamburger" icon onClick={handleSidebarToggle}>
                <Icon name="bars" size="large" />
              </Button>
            </Grid.Column>

            <Grid.Column width={8} textAlign="center">
              <Link to={homePath}>
                <img
                  src={deltaLogo}
                  alt="Logo"
                  className="logo"
                  style={{ cursor: "pointer" }}
                />
              </Link>
            </Grid.Column>

            <Grid.Column
              width={8}
              className="right-aligned-items"
              textAlign="center"
              only="computer"
            >
              <MenuMenu>
                {access === "both" && (
                  <MenuItem className="wrap-text6">
                    <Button
                      size="small"
                      onClick={() => {
                        if (currentView === "agent") {
                          setCurrentView("employee");
                          navigate("/ehome");
                        } else {
                          setCurrentView("agent");
                          navigate("/");
                        }
                      }}
                    >
                      Switch to {isAgentView ? "Employee" : "Agent"} View
                    </Button>
                  </MenuItem>
                )}

                <MenuItem className="wrap-text6" as={Link} to={homePath}>
                  <h3 className="bold">Home</h3>
                </MenuItem>

                {(access === "agent" || access === "both") && (
                  <>
                    <MenuItem className="wrap-text3" as={Link} to="/showing-page">
                      <h3 className="bold">Showing Request</h3>
                    </MenuItem>

                    <Menu.Item className="wrap-text4" as={Link} to="/form">
                      <h3 className="bold">Sales Processing Request</h3>
                    </Menu.Item>
                  </>
                )}

                <MenuItem className="wrap-text">
                  <h4>Hi, {fullName}</h4>
                  <Button onClick={signOut}>Log Out</Button>
                </MenuItem>
              </MenuMenu>
            </Grid.Column>
          </GridRow>
        </Grid>
      </Container>

      <Sidebar.Pushable as={Segment} className="content-segment">
        <Sidebar
          as={Menu}
          animation="overlay"
          icon="labeled"
          inverted
          vertical
          visible={visible}
          onHide={() => setVisible(false)}
          direction="left"
          className="sidebar-dropdown"
        >
          <MenuItem
            name={isAgentView ? "Agent Home" : "Employee Home"}
            className="popup-button-1"
            as={Link}
            to={homePath}
            onClick={handleSidebarToggle}
          />

          {access === "both" && (
            <MenuItem
              name={`Switch to ${isAgentView ? "Employee" : "Agent"} View`}
              className="popup-button-1"
              onClick={() => {
                setCurrentView((prev) =>
                  prev === "agent" ? "employee" : "agent"
                );
                setVisible(false);
              }}
            />
          )}

          {(access === "agent" || access === "both") && (
            <>
              <MenuItem
                name="Showing Request"
                className="popup-button-1"
                as={Link}
                to="/showing-page"
                onClick={handleSidebarToggle}
              />
              <MenuItem
                name="Sales Processing Request"
                className="popup-button-1"
                as={Link}
                to="/form"
                onClick={handleSidebarToggle}
              />
            </>
          )}

          {(access === "employee" || access === "both") && (
            <MenuItem
              name="Employee Tools"
              className="popup-button-1"
              as={Link}
              to="/employee-tools"
              onClick={handleSidebarToggle}
            />
          )}

          <MenuItem
            name="Log Out"
            className="popup-button-1"
            onClick={() => {
              signOut();
              window.scrollTo(0, 0);
            }}
          />
        </Sidebar>

        <Sidebar.Pusher>
          <Segment basic className="content-segment">
            <Outlet context={{ access, currentView, fullName }} />
          </Segment>
        </Sidebar.Pusher>
      </Sidebar.Pushable>

      <footer className="footer-bottom">
        <Container textAlign="center">
          <p className="foot-text">&copy; 2026 Delta Group Inc. All rights reserved.</p>
        </Container>
      </footer>
    </div>
  );
};

export default Layout;